/* SawaKart core (P1): window.SK API, <sk-reveal>, quick-add cart flow + Empire ATC banner opener,
   money formatter, cubic-bezier solver, reduced-motion + theme-editor helpers. Loaded with `defer`.
   (Comment-only edits here are also used to check that the GitHub sync reaches the theme.) */
(function () {
  'use strict';

  if (window.SK && window.SK.__core) return;

  var html = document.documentElement;
  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function cfg() { return window.skTheme || {}; }

  function route(name, fallback) {
    var t = window.Theme && window.Theme.routes;
    var s = cfg().routes;
    return (t && t[name]) || (s && s[name]) || fallback;
  }

  function reducedMotion() {
    if (mq && mq.matches) return true;
    return !!(document.body && document.body.hasAttribute('data-reduce-animations'));
  }

  function inEditor() {
    return !!((window.Shopify && window.Shopify.designMode) || html.classList.contains('in-theme-editor'));
  }

  /* ---------- IntersectionObserver helper (shared observer per margin, fires once) ---------- */
  var observers = {};

  function observeOnce(el, marginPx, cb) {
    if (!el || typeof cb !== 'function') return function () {};
    if (!('IntersectionObserver' in window)) {
      cb(null);
      return function () {};
    }
    /* a number is px on every side; a string with units is a whole rootMargin, e.g. '0px 0px -8% 0px' */
    var margin;
    if (typeof marginPx === 'string' && /[%a-z]/i.test(marginPx)) {
      margin = marginPx.trim();
    } else {
      var m = parseFloat(marginPx);
      if (!isFinite(m)) m = 0;
      margin = m + 'px';
    }
    var key = margin;
    var rec = observers[key];
    if (!rec) {
      rec = { map: new Map() };
      rec.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          /* in view, or already scrolled past (mtstorelb: boundingClientRect.bottom < 0) */
          if (!entry.isIntersecting && entry.boundingClientRect.bottom >= 0) return;
          var list = rec.map.get(entry.target);
          rec.io.unobserve(entry.target);
          rec.map.delete(entry.target);
          if (list) list.forEach(function (fn) { fn(entry); });
        });
      }, { rootMargin: margin, threshold: 0 });
      observers[key] = rec;
    }
    var cbs = rec.map.get(el);
    if (!cbs) {
      cbs = [];
      rec.map.set(el, cbs);
      rec.io.observe(el);
    }
    cbs.push(cb);
    return function () {
      var list = rec.map.get(el);
      if (!list) return;
      var i = list.indexOf(cb);
      if (i > -1) list.splice(i, 1);
      if (!list.length) {
        rec.map.delete(el);
        rec.io.unobserve(el);
      }
    };
  }

  /* ---------- Money (D11) ---------- */
  function groupDigits(value, decimals, thousands, decimal) {
    var fixed = (Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)).toFixed(decimals);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
  }

  function formatMoney(cents) {
    var n = typeof cents === 'string' ? parseFloat(cents.replace(/[^\d.-]/g, '')) : Number(cents);
    if (!isFinite(n)) n = 0;
    var value = n / 100;
    var c = cfg();
    // Always the store's Shopify money format, matching Liquid's `money` filter.
    var format = String(c.moneyFormat || '${{amount}}');
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, type) {
      switch (type) {
        case 'amount_no_decimals': return groupDigits(value, 0, ',', '.');
        case 'amount_with_comma_separator': return groupDigits(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return groupDigits(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return groupDigits(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return groupDigits(value, 0, ' ', ',');
        case 'amount_with_space_separator': return groupDigits(value, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return groupDigits(value, 2, ' ', '.');
        default: return groupDigits(value, 2, ',', '.');
      }
    });
  }

  /* ---------- Cubic-bezier solver (UnitBezier) ---------- */
  function bezier(x1, y1, x2, y2) {
    var cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
    var cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    function sampleX(t) { return ((ax * t + bx) * t + cx) * t; }
    function sampleY(t) { return ((ay * t + by) * t + cy) * t; }
    function sampleDX(t) { return (3 * ax * t + 2 * bx) * t + cx; }
    function solveX(x) {
      var t = x, i, d, x2v;
      for (i = 0; i < 8; i++) {
        x2v = sampleX(t) - x;
        if (Math.abs(x2v) < 1e-7) return t;
        d = sampleDX(t);
        if (Math.abs(d) < 1e-6) break;
        t -= x2v / d;
      }
      var lo = 0, hi = 1;
      t = x;
      if (t < lo) return lo;
      if (t > hi) return hi;
      while (lo < hi) {
        x2v = sampleX(t);
        if (Math.abs(x2v - x) < 1e-7) return t;
        if (x > x2v) lo = t; else hi = t;
        t = (hi - lo) / 2 + lo;
        if (hi - lo < 1e-9) break;
      }
      return t;
    }
    return function (t) {
      if (t <= 0) return 0;
      if (t >= 1) return 1;
      return sampleY(solveX(t));
    };
  }

  /* ---------- Empire state-attribute animation protocol ---------- */
  function animateTo(el, to) {
    return new Promise(function (resolve) {
      var from = el.getAttribute('data-animation-state') || 'closed';
      if (from === to) {
        el.removeAttribute('data-animation');
        resolve();
        return;
      }
      requestAnimationFrame(function () {
        el.setAttribute('data-animation', from + '=>' + to);
        requestAnimationFrame(function () {
          var done = false;
          var timer;
          function finish() {
            if (done) return;
            done = true;
            clearTimeout(timer);
            el.removeEventListener('transitionend', onEnd);
            el.removeAttribute('data-animation');
            resolve();
          }
          function onEnd(e) { if (e.target === el) finish(); }
          el.addEventListener('transitionend', onEnd);
          timer = setTimeout(finish, 800);
          el.setAttribute('data-animation-state', to);
        });
      });
    });
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (n) {
      return n.offsetWidth > 0 || n.offsetHeight > 0 || n.getClientRects().length > 0;
    });
  }

  /* ---------- ATC banner (Empire [data-atc-banner] template, restyled as the FE drawer by P5) ---------- */
  var openBanner = null;

  function setAnnouncementIndex(value) {
    var bar = document.querySelector('[data-announcement-bar]');
    if (bar) bar.style.setProperty('--index-announcement-bar', value);
  }

  function sizedImage(src, width) {
    if (!src) return '';
    try {
      var u = new URL(src, window.location.href);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return src;
    }
  }

  function setText(root, selector, text) {
    var n = root.querySelector(selector);
    if (n) n.textContent = text;
    return n;
  }

  function fillBanner(flyOut, item, cart, quantity) {
    var imgWrap = flyOut.querySelector('[data-atc-banner-product-image]');
    if (imgWrap && item.image) {
      var img = document.createElement('img');
      img.src = sizedImage(item.image, 200);
      img.alt = item.product_title || '';
      img.width = 200;
      img.height = 200;
      imgWrap.textContent = '';
      imgWrap.appendChild(img);
    }
    setText(flyOut, '[data-atc-banner-product-title]', item.product_title || item.title || '');

    var opts = item.variant_options || [];
    if (opts.length && opts[0] !== 'Title' && opts[0] !== 'Default Title') {
      setText(flyOut, '[data-atc-banner-product-options]', opts.join(', '));
    }
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      setText(flyOut, '[data-atc-banner-product-subscription-title]', item.selling_plan_allocation.selling_plan.name);
    }
    setText(flyOut, '[data-atc-banner-product-price-quantity]', quantity + ' × ');

    var line = null;
    (cart.items || []).forEach(function (it) {
      if (it.key === item.key || (!line && it.id === item.id)) line = line || it;
    });
    line = line || item;

    var priceValue = setText(flyOut, '[data-atc-banner-product-price-value]', formatMoney(line.original_price));
    var priceDiscounted = flyOut.querySelector('[data-atc-banner-product-price-discounted]');
    if (priceDiscounted) {
      if (line.final_price < line.original_price) {
        priceDiscounted.textContent = formatMoney(line.final_price);
        priceDiscounted.classList.remove('hidden');
        if (priceValue) priceValue.classList.add('original-price');
      } else {
        priceDiscounted.classList.add('hidden');
        if (priceValue) priceValue.classList.remove('original-price');
      }
    }

    var discounts = flyOut.querySelector('[data-atc-banner-product-discounts]');
    if (discounts) {
      var allocations = line.line_level_discount_allocations || [];
      if (allocations.length && discounts.firstElementChild) {
        var tpl = discounts.firstElementChild.cloneNode(true);
        discounts.textContent = '';
        allocations.forEach(function (a) {
          var li = tpl.cloneNode(true);
          setText(li, '.discount-title', (a.discount_application && a.discount_application.title) || '');
          setText(li, '.discount-amount', formatMoney(a.amount));
          discounts.appendChild(li);
        });
        discounts.classList.remove('hidden');
      } else {
        discounts.classList.add('hidden');
      }
    }

    var unit = flyOut.querySelector('[data-atc-banner-unit-price]');
    if (unit && line.unit_price_measurement) {
      var m = line.unit_price_measurement;
      var measure = m.reference_value === 1 ? m.reference_unit : m.reference_value + m.reference_unit;
      unit.innerHTML = unit.innerHTML
        .replace('** total_quantity **', String(m.quantity_value) + m.quantity_unit)
        .replace('** unit_price **', formatMoney(line.unit_price))
        .replace('** unit_measure **', measure);
      unit.classList.remove('hidden');
    }

    setText(flyOut, '[data-atc-banner-cart-subtotal]', formatMoney(cart.total_price));
    setText(flyOut, '[data-atc-banner-cart-button] span', String(cart.item_count));
  }

  function closeBanner() {
    var state = openBanner;
    if (!state || state.closing) return Promise.resolve();
    state.closing = true;
    state.cleanup();
    if (state.returnFocus && document.contains(state.returnFocus)) {
      try { state.returnFocus.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
    }
    return animateTo(state.el, 'closed').then(function () {
      state.el.remove();
      if (openBanner === state) {
        openBanner = null;
        setAnnouncementIndex('');
      }
    });
  }

  function showBanner(item, cart, quantity, trigger) {
    var template = document.querySelector('[data-templates] [data-atc-banner]');
    var host = document.querySelector('[data-site-header]');
    if (!template || !host) return false;

    var flyOut = template.cloneNode(true);
    flyOut.setAttribute('data-sk-atc-banner', '');
    flyOut.setAttribute('data-animation-state', 'closed');
    fillBanner(flyOut, item, cart, quantity);

    var state = { el: flyOut, closing: false, returnFocus: trigger || document.activeElement };

    function onClick(e) {
      if (e.target === flyOut) closeBanner();
    }
    function onCloseButton(e) {
      e.preventDefault();
      closeBanner();
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        closeBanner();
        return;
      }
      if (e.key !== 'Tab') return;
      var list = focusables(flyOut);
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (!flyOut.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    function onCloseAll() { closeBanner(); }

    var closeBtn = flyOut.querySelector('[data-atc-banner-close]');
    if (closeBtn) closeBtn.addEventListener('click', onCloseButton);
    flyOut.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);

    state.cleanup = function () {
      if (closeBtn) closeBtn.removeEventListener('click', onCloseButton);
      flyOut.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('closeFlyouts', onCloseAll);
    };

    setAnnouncementIndex('1100');
    host.appendChild(flyOut);
    openBanner = state;
    document.addEventListener('closeFlyouts', onCloseAll);

    animateTo(flyOut, 'open').then(function () {
      if (openBanner !== state || state.closing) return;
      var list = focusables(flyOut);
      if (list.length) {
        try { list[0].focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
    });
    return true;
  }

  function showError(message) {
    var template = document.querySelector('[data-templates] [data-message-banner]');
    var modal = document.querySelector('.modal-loaded .modal-inner');
    var host = modal || document.querySelector('[data-site-header]');
    if (!template || !host) return;
    var banner = template.cloneNode(true);
    banner.setAttribute('data-sk-message-banner', '');
    banner.setAttribute('data-animation-state', 'closed');
    banner.classList.add('message--error');
    setText(banner, '[data-message-banner-content]', message);
    host.appendChild(banner);

    var closed = false;
    function close() {
      if (closed) return;
      closed = true;
      document.removeEventListener('click', onDoc);
      window.removeEventListener('keydown', onKey);
      animateTo(banner, 'closed').then(function () { banner.remove(); });
    }
    function onDoc(e) {
      if (!banner.contains(e.target) || e.target.closest('[data-message-banner-close]')) close();
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('click', onDoc);
    window.addEventListener('keydown', onKey);
    animateTo(banner, 'open');
  }

  /* ---------- Cart ---------- */
  function setBusy(trigger, busy) {
    if (!trigger) return;
    if (busy) {
      trigger.setAttribute('aria-busy', 'true');
      if ('disabled' in trigger) trigger.disabled = true;
      trigger.classList.add('is-busy');
    } else {
      trigger.removeAttribute('aria-busy');
      if ('disabled' in trigger) trigger.disabled = false;
      trigger.classList.remove('is-busy');
    }
  }

  /* One request: /cart/add.js also returns the sidebar's markup (sections), so the sidebar opens as
     soon as the add answers; the header badge's /cart.js runs alongside (SK-56: was three in a row). */
  function add(variantId, qty, trigger) {
    var quantity = parseInt(qty, 10) || 1;
    var id = Number(variantId);
    var drawer = SK.cartDrawer && typeof SK.cartDrawer.open === 'function' ? SK.cartDrawer : null;
    setBusy(trigger, true);
    var added;
    var body = { items: [{ id: id, quantity: quantity }] };
    if (drawer) { body.sections = ['sk-cart-drawer']; body.sections_url = route('cart_url', '/cart'); }
    return fetch(route('cart_add_url', '/cart/add') + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok || data.status) {
            var err = new Error('cart/add');
            err.skDescription = data && typeof data.description === 'string' ? data.description : '';
            throw err;
          }
          added = data;
          document.dispatchEvent(new Event('closeFlyouts'));
          if (drawer) {
            var markup = data.sections && data.sections['sk-cart-drawer'];
            drawer.open({ trigger: trigger, html: markup || undefined });
            setBusy(trigger, false);
          }
          return fetch(route('cart_url', '/cart') + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('cart');
        return res.json();
      })
      .then(function (cart) {
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
        if (!drawer) {
          var item = (added && added.items && added.items[0]) || added || {};
          showBanner(item, cart, quantity, trigger);
        }
        setBusy(trigger, false);
        return cart;
      })
      .catch(function (err) {
        setBusy(trigger, false);
        var fallback = (cfg().strings && cfg().strings.atcError) || "Couldn't add to cart. Please try again.";
        showError((err && err.skDescription) || fallback);
        throw err;
      });
  }

  /* ---------- Public API ---------- */
  var SK = window.SK = window.SK || {};
  SK.__core = true;
  SK.reducedMotion = reducedMotion;
  Object.defineProperty(SK, 'inEditor', { get: inEditor, configurable: true, enumerable: true });
  SK.observeOnce = observeOnce;
  SK.formatMoney = formatMoney;
  SK.bezier = bezier;
  SK.cart = SK.cart || {};
  SK.cart.add = add;
  SK.cart.close = closeBanner;

  /* ---------- <sk-reveal> ---------- */
  function reveal(el) { el.classList.add('is-in'); }

  if (!customElements.get('sk-reveal')) {
    customElements.define('sk-reveal', class extends HTMLElement {
      connectedCallback() {
        if (this.classList.contains('is-in')) return;
        if (inEditor()) {
          reveal(this);
          return;
        }
        var self = this;
        /* default trigger: 8% into the viewport (mtstorelb); sections may set a px data-margin */
        this._skOff = observeOnce(this, this.getAttribute('data-margin') || '0px 0px -8% 0px', function () {
          self._skOff = null;
          reveal(self);
        });
      }
      disconnectedCallback() {
        if (this._skOff) {
          this._skOff();
          this._skOff = null;
        }
      }
    });
  }

  /* ---------- Delegated quick-add ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-sk-atc]') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    var id = btn.getAttribute('data-variant-id');
    if (!id) return;
    add(id, btn.getAttribute('data-quantity') || 1, btn).catch(function () { /* surfaced by the error banner */ });
  });

  /* ---------- Sign in on the storefront: Shopify's <shopify-account> sheet (10 Oct 2026) ----------
     [data-sk-account-open="<selector>"] buttons open the sheet of that component (default: the
     header's); without a working component the button's own href (the sign-in page) takes over.
     The sign-in page opens its sheet by itself; a signed-in visitor there goes on to My orders. */
  function accountComponent(sel) {
    var el = (sel && document.querySelector(sel)) || document.querySelector('.site-header shopify-account') || document.querySelector('shopify-account');
    return el && typeof el.showModal === 'function' ? el : null;
  }
  function openAccount(sel) {
    var el = accountComponent(sel);
    if (!el) return false;
    var drawer = document.querySelector('sk-mobile-drawer');
    if (drawer && typeof drawer.close === 'function') { try { drawer.close(true); } catch (err) { /* not open */ } }
    try { el.showModal(); } catch (err) { return false; }
    return true;
  }
  SK.openAccount = openAccount;
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-sk-account-open]') : null;
    if (!btn) return;
    if (openAccount(btn.getAttribute('data-sk-account-open'))) { e.preventDefault(); e.stopPropagation(); }
  }, true);
  /* The sign-in page: Shopify's panel is shown inline in the page. The component opens it as a modal
     dialog; we re-open the same dialog non-modal, so it sits in the column under the text with no
     backdrop. It builds itself in steps (a skeleton, the email form, then Sign in with Shop), changing
     height each time, so until it is complete it stays unseen and inert (no focus, no scroll) over a
     placeholder card of its final size, then fades in once (.is-ready): nothing jumps (SK-54).
     Opening it the component also locks the page's scrolling (<html> overflow/touch-action), sets a
     --dialog-min-height for its phone drawer, and closes it whenever it thinks it is hidden (while the
     panel is unseen). Inline here its dialog's showModal() is a plain show(), the page's styles are put
     back right after, and close() does nothing; all undone if the page falls back. Its X is hidden; if
     it closes anyway it is shown again. Without the component the link to Shopify's sign-in shows
     (.is-fallback). */
  function signinPage() {
    var pg = document.querySelector('[data-sk-signin-page]');
    if (!pg) return;
    var next = pg.getAttribute('data-sk-signed-in');
    if (next) { window.location.replace(next); return; }
    var box = pg.querySelector('[data-sk-signin]');
    var el = box && box.querySelector('.sks-account');
    if (!el) return;
    var state = 'loading';
    var undo = null;
    var settle = function (to) {
      if (state !== 'loading') return;
      state = to;
      if (to === 'fallback' && undo) undo();
      /* what follows the panel: if the finished panel is not the placeholder's height, it slides, not jumps */
      var below = [];
      for (var n = box.nextElementSibling; n; n = n.nextElementSibling) if (n.getClientRects().length) below.push(n);
      var before = below.length ? below[0].getBoundingClientRect().top : 0;
      el.inert = false;
      box.classList.remove('is-loading');
      box.classList.add(to === 'ready' ? 'is-ready' : 'is-fallback');
      if (to !== 'ready' || !below.length || !below[0].animate || reducedMotion()) return;
      var shift = before - below[0].getBoundingClientRect().top;
      if (Math.abs(shift) < 2) return;
      below.forEach(function (n) {
        n.animate([{ transform: 'translateY(' + shift + 'px)' }, { transform: 'none' }], { duration: 450, easing: 'cubic-bezier(.16, 1, .3, 1)' });
      });
    };
    if (!(window.customElements && customElements.whenDefined)) { settle('fallback'); return; }
    var late = setTimeout(function () { settle('fallback'); }, 8000);
    customElements.whenDefined('shopify-account').then(function () {
      clearTimeout(late);
      if (state !== 'loading' || typeof el.showModal !== 'function') { settle('fallback'); return; }
      var dialog = function () { return el.shadowRoot && el.shadowRoot.querySelector('dialog'); };
      var find = function (root, test) {
        var all = root.querySelectorAll('*');
        for (var i = 0; i < all.length; i++) {
          if (test(all[i])) return all[i];
          if (all[i].shadowRoot) { var hit = find(all[i].shadowRoot, test); if (hit) return hit; }
        }
        return null;
      };
      var isSkeleton = function (n) { return n.classList.contains('account__skeleton-line'); };
      var isEmail = function (n) { return n.tagName === 'INPUT' && n.type === 'email'; };
      var isShop = function (n) {
        return n.tagName === 'SHOP-LOGIN' && !!n.shadowRoot &&
          [].some.call(n.shadowRoot.querySelectorAll('button'), function (b) { return b.offsetHeight > 0; });
      };
      var inline = function () {
        var d = dialog();
        if (!d) return false;
        if (d.open && d.matches(':modal')) d.close();
        if (!d.open) d.show();
        pg.classList.add('is-inline');
        if (!d.skInline) {
          d.skInline = true;
          /* the dialog's own close does not reach the host, so listen on the dialog itself */
          d.addEventListener('close', function () { setTimeout(function () { if (!d.open) d.show(); }, 0); });
          try {
            var sheet = new CSSStyleSheet();
            sheet.replaceSync('.account__close { display: none !important; }');
            el.shadowRoot.adoptedStyleSheets = el.shadowRoot.adoptedStyleSheets.concat(sheet);
          } catch (err) { /* the X stays; closing just shows the panel again */ }
        }
        return true;
      };
      /* complete: no skeleton and Sign in with Shop drawn, or (no Shop) the email form still for 2.5s; at most 6s */
      var watch = function () {
        var t0 = Date.now(), still = t0, lastH = -1;
        (function tick() {
          if (state !== 'loading') return;
          var d = dialog(), now = Date.now();
          var h = d && d.open ? d.offsetHeight : 0;
          if (h !== lastH) { lastH = h; still = now; }
          var busy = !d || d.classList.contains('closing') || find(el.shadowRoot, isSkeleton);
          if (h > 0 && !busy && ((now - still >= 150 && find(el.shadowRoot, isShop)) || (now - still >= 2500 && find(el.shadowRoot, isEmail)))) { settle('ready'); return; }
          if (now - t0 >= 6000) { settle(h > 0 ? 'ready' : 'fallback'); return; }
          setTimeout(tick, 100);
        })();
      };
      el.inert = true;
      var d0 = dialog();
      var ownClose = Object.prototype.hasOwnProperty.call(el, 'close');
      el.close = function () { return el; };
      if (d0) {
        d0.showModal = function () {
          var st = html.style, was = [st.overflow, st.touchAction, st.scrollbarGutter];
          if (!d0.open) d0.show();
          Promise.resolve().then(function () {
            st.overflow = was[0];
            st.touchAction = was[1];
            st.scrollbarGutter = was[2];
            d0.style.removeProperty('--dialog-min-height');
          });
        };
      }
      undo = function () {
        if (d0) delete d0.showModal;
        if (!ownClose) delete el.close;
      };
      try { el.showModal(); } catch (err) { settle('fallback'); return; }
      requestAnimationFrame(function () {
        if (inline()) { watch(); return; }
        setTimeout(function () { if (inline()) watch(); else settle('fallback'); }, 400);
      });
    }).catch(function () { settle('fallback'); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', signinPage);
  else signinPage();
  /* the theme editor re-renders the section: set the new copy up again */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-sk-signin-page]')) signinPage();
  });

  /* ---------- Reduced motion (media query + Empire's body[data-reduce-animations]) ---------- */
  function syncMotion() {
    var reduced = reducedMotion();
    html.classList.toggle('sk-reduced-motion', reduced);
    document.dispatchEvent(new CustomEvent('sk:reduced-motion', { detail: { reduced: reduced } }));
  }
  html.classList.toggle('sk-reduced-motion', reducedMotion());
  if (mq) {
    if (mq.addEventListener) mq.addEventListener('change', syncMotion);
    else if (mq.addListener) mq.addListener(syncMotion);
  }
  document.addEventListener('DOMContentLoaded', function () {
    html.classList.toggle('sk-reduced-motion', reducedMotion());
  });

  /* ---------- Endless animations wait while off-screen (SK-63) ----------
     The brand strip, the About page strip and the hero's timer bar get .is-offscreen when they are more than
     200px outside the screen; their CSS pauses the animation, so a phone does no work for what nobody sees. */
  (function () {
    if (!('IntersectionObserver' in window)) return;
    var SEL = '.sk-brands__strip, .sk-story-marquee__viewport, sk-hero';
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { e.target.classList.toggle('is-offscreen', !e.isIntersecting); });
    }, { rootMargin: '200px 0px' });
    function watch(root) {
      if (!root || !root.querySelectorAll) return;
      Array.prototype.forEach.call(root.querySelectorAll(SEL), function (el) {
        if (el._skOffscreen) return;
        el._skOffscreen = true;
        io.observe(el);
      });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { watch(document); });
    else watch(document);
    document.addEventListener('shopify:section:load', function (e) { watch(e.target); });
  })();

  /* ---------- Theme editor helpers ---------- */
  function revealWithin(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches('sk-reveal')) reveal(root);
    root.querySelectorAll('sk-reveal').forEach(reveal);
  }
  ['shopify:section:load', 'shopify:section:select', 'shopify:block:select'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var target = e.target;
      revealWithin(target);
      if (target && target.closest) {
        var host = target.closest('sk-reveal');
        if (host) reveal(host);
      }
    });
  });
})();
